import type { ForqSupabaseClient } from './client';
import type {
  CreateRentalOrderInput,
  FleetMachine,
  RentalBilling,
  RentalGateway,
  RentalOrder,
  RentalStatus,
  ReturnItemInput,
  TransportStatus,
  UpdateRentalOrderInput,
} from '@forq/rental-data';
import { computeFinalBilling, computeMonthlyBilling } from '@forq/rental-data';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

/**
 * Der EXCLUDE-Constraint auf `rental_occupancies` ist das letzte Netz gegen die Doppelvermietung —
 * er greift auch bei zwei gleichzeitigen Buchungen, an denen jede Vorprüfung vorbeiläuft. Seine
 * Meldung ist allerdings englischer Postgres-Rohtext („conflicting key value violates exclusion
 * constraint …"), und die gehört nicht vor einen Disponenten.
 *
 * Erkannt wird am FEHLERCODE 23P01, nicht am Wortlaut: eine Meldung kann sich mit jeder
 * Postgres-Fassung ändern, ein SQLSTATE nicht. Die Vorprüfungen in den RPCs werfen ihren eigenen
 * deutschen Klartext (mit der Nummer des belegenden Auftrags) — der bleibt unangetastet.
 */
function rentalMessage(error: { readonly message: string; readonly code?: string } | null): string {
  if (error?.code === '23P01') {
    return 'Die Maschine wurde soeben anderweitig vermietet. Bitte Zeitraum oder Maschine neu wählen.';
  }
  return messageOf(error);
}

/**
 * Mietpark gegen Supabase.
 *
 * Die RPCs geben den Mietauftrag als jsonb GENAU in der Form von RentalOrder zurück (camelCase,
 * Daten als YYYY-MM-DD) -- deshalb reicht hier ein Cast statt einer Feld-für-Feld-Übersetzung.
 * Der Preis dafür steht im Schema: rental_order_json baut die Form, dieser Adapter verlässt sich
 * darauf. Ein Test in rental.test.ts prüft, dass die Gestalt stimmt.
 *
 * Abrechnung: die ZEILEN rechnet @forq/rental-data (dort getestet), der RPC speichert nur den
 * Zustand. billMonth lädt dazu den Auftrag, berechnet Zeitraum + Zeilen, persistiert das neue
 * billedThrough und gibt den frischen Auftrag zurück.
 */
export class SupabaseRentalGateway implements RentalGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  private async rpc(functionName: string, args: Record<string, unknown>): Promise<unknown> {
    const { data, error } = await this.client.rpc(functionName, args);
    if (error) throw new SupabaseOperationError(`rental.${functionName}`, rentalMessage(error));
    return data;
  }

  async listFleet(tenantId: string): Promise<readonly FleetMachine[]> {
    const data = await this.rpc('rental_list_fleet', { p_tenant_id: tenantId });
    return (data ?? []) as readonly FleetMachine[];
  }

  async listRentalOrders(tenantId: string): Promise<readonly RentalOrder[]> {
    const data = await this.rpc('rental_list', { p_tenant_id: tenantId });
    return (data ?? []) as readonly RentalOrder[];
  }

  async createRentalOrder(input: CreateRentalOrderInput): Promise<RentalOrder> {
    const { tenantId, ...payload } = input;
    return this.rpc('rental_create', { p_tenant_id: tenantId, p_payload: payload }) as Promise<RentalOrder>;
  }

  async updateRentalOrder(input: UpdateRentalOrderInput): Promise<RentalOrder> {
    const { tenantId, rentalId, ...payload } = input;
    return this.rpc('rental_update', { p_tenant_id: tenantId, p_rental_id: rentalId, p_payload: payload }) as Promise<RentalOrder>;
  }

  async transitionRental(input: { tenantId: string; rentalId: string; toStatus: RentalStatus }): Promise<RentalOrder> {
    return this.rpc('rental_transition', {
      p_tenant_id: input.tenantId,
      p_rental_id: input.rentalId,
      p_to_status: input.toStatus,
    }) as Promise<RentalOrder>;
  }

  async updateTransport(input: {
    tenantId: string; rentalId: string; transportId: string;
    plannedDate?: string; driverName?: string; status?: TransportStatus;
  }): Promise<RentalOrder> {
    return this.rpc('rental_update_transport', {
      p_tenant_id: input.tenantId,
      p_rental_id: input.rentalId,
      p_transport_id: input.transportId,
      p_planned_date: input.plannedDate ?? null,
      p_driver_name: input.driverName ?? null,
      p_status: input.status ?? null,
    }) as Promise<RentalOrder>;
  }

  async captureReturn(input: {
    tenantId: string; rentalId: string; actualReturnDate: string; items: readonly ReturnItemInput[];
  }): Promise<RentalOrder> {
    return this.rpc('rental_capture_return', {
      p_tenant_id: input.tenantId,
      p_rental_id: input.rentalId,
      p_actual_return_date: input.actualReturnDate,
      p_items: input.items,
    }) as Promise<RentalOrder>;
  }

  async terminateEarly(input: { tenantId: string; rentalId: string; terminationDate: string }): Promise<RentalOrder> {
    return this.rpc('rental_terminate_early', {
      p_tenant_id: input.tenantId,
      p_rental_id: input.rentalId,
      p_termination_date: input.terminationDate,
    }) as Promise<RentalOrder>;
  }

  async billMonth(input: { tenantId: string; rentalId: string }): Promise<{ order: RentalOrder; billing: RentalBilling }> {
    const order = await this.loadOrder(input.tenantId, input.rentalId);
    // Zeitraum + Zeilen in TS (die getestete Quelle der Wahrheit) -- wirft die fachlichen Fehler
    // (nichts abzurechnen, kein Einsatz) BEVOR etwas persistiert wird.
    const { to, billing } = computeMonthlyBilling(order);
    const updated = await this.rpc('rental_bill_month', {
      p_tenant_id: input.tenantId,
      p_rental_id: input.rentalId,
      p_billed_through: to,
    }) as RentalOrder;
    return { order: updated, billing };
  }

  async billFinal(input: { tenantId: string; rentalId: string }): Promise<{ order: RentalOrder; billing: RentalBilling }> {
    const order = await this.loadOrder(input.tenantId, input.rentalId);
    const billing = computeFinalBilling(order);
    const updated = await this.rpc('rental_bill_final', {
      p_tenant_id: input.tenantId,
      p_rental_id: input.rentalId,
    }) as RentalOrder;
    return { order: updated, billing };
  }

  /** Lädt genau einen Auftrag für die app-seitige Abrechnungsberechnung. */
  private async loadOrder(tenantId: string, rentalId: string): Promise<RentalOrder> {
    const orders = await this.listRentalOrders(tenantId);
    const order = orders.find((entry) => entry.id === rentalId);
    if (!order) throw new SupabaseOperationError('rental.loadOrder', 'Der Mietauftrag wurde nicht gefunden.');
    return order;
  }
}
